// Shared shaping for the GL Variance Drivers table (used by the Commentary tab and the PDF).
//
// Groups the material unfavorable misses (Tier 1 red / Tier 2 yellow that carry an
// explanation) by department, ordered critical-first then by magnitude, and returns a
// rollup of the sub-materiality "noise" lines. Mirrors buildVarianceDriversSection on
// the backend so the table and the stored narrative stay in lockstep.

const acctVal = (n) => {
  const s = String(n || '').replace(/[^0-9.]/g, '');
  const p = parseFloat(s);
  return Number.isFinite(p) ? p : Number.MAX_SAFE_INTEGER;
};
const cmpAcct = (a, b) => {
  const d = acctVal(a) - acctVal(b);
  return d !== 0 ? d : String(a || '').localeCompare(String(b || ''), undefined, { numeric: true });
};

// "ROOMS REVENUE" -> "Rooms Revenue"
export const deptTitle = (s) => String(s || '').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

export function groupVarianceDrivers(critiqueLines) {
  const lines = critiqueLines || [];
  const flagged = lines.filter((l) =>
    (l.flagSeverity === 'red' || l.flagSeverity === 'yellow') && l.aiNarrative && String(l.aiNarrative).trim()
  );

  const byDept = {};
  for (const l of flagged) {
    const dept = l.department || 'Other';
    (byDept[dept] = byDept[dept] || []).push(l);
  }

  const tierRank = (l) => (l.flagSeverity === 'red' ? 0 : 1);

  const departments = Object.keys(byDept)
    .sort((a, b) => {
      const fa = byDept[a].slice().sort((x, y) => cmpAcct(x.glAccountCode, y.glAccountCode))[0];
      const fb = byDept[b].slice().sort((x, y) => cmpAcct(x.glAccountCode, y.glAccountCode))[0];
      return cmpAcct(fa?.glAccountCode, fb?.glAccountCode) || a.localeCompare(b);
    })
    .map((dept) => ({
      department: dept,
      rows: byDept[dept]
        .slice()
        .sort((x, y) => {
          const t = tierRank(x) - tierRank(y);
          if (t !== 0) return t;
          return Math.abs(Number(y.varianceBudget || 0)) - Math.abs(Number(x.varianceBudget || 0));
        })
        .map((l) => {
          const actual = Number(l.amountActual || 0);
          const budget = Number(l.amountBudget || 0);
          const variance = actual - budget; // actual − budget, matches the statement view
          const favorable = Number(l.varianceBudget || 0) >= 0;
          const pct = budget !== 0 ? (variance / Math.abs(budget)) * 100 : null;
          return {
            id: l.id,
            accountNumber: l.glAccountCode,
            accountName: l.glAccountName,
            actual,
            budget,
            variance,
            pct,
            favorable,
            tier: l.flagSeverity === 'red' ? 'Critical' : 'Attention',
            commentary: String(l.aiNarrative).trim(),
          };
        }),
    }));

  // Budget-split (offset) siblings are a budget-coding artifact, not
  // variances — suppressed entirely, never reported (not even as noise).
  const noise = lines.filter((l) => !l.flagSeverity && l.flagCategory !== 'offset' && Math.abs(Number(l.varianceBudget) || 0) > 0);
  const immaterial = noise.length
    ? { count: noise.length, net: noise.reduce((s, l) => s + (Number(l.varianceBudget) || 0), 0) }
    : null;

  return { departments, immaterial };
}
